/**
 * Spec SASADM-0086 (U1) — per-tenant Turnstile / Captcha admin client
 * (saas-auth SASATH-0149 config CRUD · SASATH-0151 Cloudflare automation ·
 * SASATH-0152 per-tenant enforce).
 *
 * Seven SUPER_ADMIN-gated methods on saas-auth's tenant-scoped
 * `/api/saas-admin/tenants/{tenantId}/captcha` surface. saas-admin stores
 * nothing for captcha — it calls saas-auth and renders config + widget state.
 *
 * ENVELOPE: build-to-contract on the standard `ApiResponse<T>` envelope
 * (matching ghlIntegration/resourceLimits/operators). Reads use the strict
 * `request()` + `unwrap()` pattern; `get` tolerates a 404 as the empty state.
 *
 * SECRET HYGIENE: the Turnstile secret is write-only. It is never returned by
 * `get` (only `secretKeySet`/`secretKeySuffix`) and only ever sent on `set` when the
 * admin types a new value — the caller omits `secretKey` otherwise, and a PUT
 * NEVER clears the secret (DELETE is the only clear path).
 *
 * CSRF: `/api/saas-admin/**` mutations get bearer + `X-CSRF-Token` from the
 * HTTP layer automatically; the POST bodies are `{}` (CSRF is header-based).
 */
import { request, unwrap } from '../client';
import { ApiError } from '../errors';
import type {
  ApiResponse,
  CaptchaConfigDto,
  CaptchaWidgetDto,
  ProvisionCaptchaResponse,
  SetCaptchaConfigRequest,
} from '../types';

const base = (tenantId: number) => `/api/saas-admin/tenants/${tenantId}/captcha`;

export const captchaApi = {
  /**
   * Read the tenant's captcha config (secret masked). A 404 means the tenant
   * has no config — a normal empty state mapped to `null` rather than an error.
   * Any other failure (incl. 403) propagates.
   */
  async get(tenantId: number): Promise<CaptchaConfigDto | null> {
    try {
      const res = await request<ApiResponse<CaptchaConfigDto>>('GET', base(tenantId));
      return unwrap(res);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  /**
   * Create or replace the tenant's captcha config (PUT upsert). The caller must
   * omit `secretKey` from `req` when the admin left the secret field blank — a
   * PUT never clears the secret, so an omitted/blank secret keeps the existing
   * one. Returns the masked config.
   */
  async set(tenantId: number, req: SetCaptchaConfigRequest): Promise<CaptchaConfigDto> {
    const res = await request<ApiResponse<CaptchaConfigDto>>('PUT', base(tenantId), {
      json: req,
    });
    return unwrap(res);
  },

  /** Delete the tenant's captcha config entirely (the only way to clear the secret). */
  async remove(tenantId: number): Promise<void> {
    await request<unknown>('DELETE', base(tenantId));
  },

  /**
   * Provision a Cloudflare Turnstile widget seeded with the tenant's APP+LOGIN
   * hostnames. Idempotent: an already-provisioned tenant returns
   * `{ alreadyProvisioned: true }`. Errors: `400 TOO_MANY_HOSTNAMES` (>10
   * APP/LOGIN hosts) · `400 NO_HOSTNAMES` (no APP/LOGIN host).
   */
  async provision(tenantId: number): Promise<ProvisionCaptchaResponse> {
    const res = await request<ApiResponse<ProvisionCaptchaResponse>>(
      'POST',
      `${base(tenantId)}/provision`,
      { json: {} },
    );
    return unwrap(res);
  },

  /** Rotate the widget secret and store it. `409 WIDGET_NOT_PROVISIONED`. */
  async rotateSecret(tenantId: number): Promise<void> {
    await request<unknown>('POST', `${base(tenantId)}/rotate-secret`, { json: {} });
  },

  /**
   * Push the tenant's CURRENT APP+LOGIN hostnames to the widget.
   * `409 WIDGET_NOT_PROVISIONED` · `400 TOO_MANY_HOSTNAMES`.
   */
  async syncHostnames(tenantId: number): Promise<void> {
    await request<unknown>('POST', `${base(tenantId)}/sync-hostnames`, { json: {} });
  },

  /** Read the widget state backing the hostnames ✓/✗ indicator. */
  async getWidget(tenantId: number): Promise<CaptchaWidgetDto> {
    const res = await request<ApiResponse<CaptchaWidgetDto>>('GET', `${base(tenantId)}/widget`);
    return unwrap(res);
  },
};
